/**
 * Spell damage (Foundry: SpellPF2e#getDamage): the heightened damage entries as base damage, the
 * spellcasting attribute where the entry applies it, every damage synthetic whose selector names
 * the spell's domains (`spell-damage`, `<slug>-damage`, `attack-spell-damage`), damage alterations,
 * and Foundry's formula builder for a success and a critical success. Nothing is rolled here (#19).
 */
import { applyDamageAlteration, type DamageDice } from "./damage-dice";
import { createDamageFormula, type BaseDamageData, type Degree, type FormulaLabels } from "./damage-formula";
import type { Entry } from "../../shared/snapshot-format";
import { EngineItem } from "./items";
import { damageTypeLabel, localize } from "./labels";
import { createModifier, type Modifier } from "./modifiers";
import type { HeightenedSpell } from "./spells";
import { parseSimpleFormula } from "./spells";
import type { CastingSource } from "./spellcasting";
import type { EngineState } from "./state";
import { extractDamageAlterations, extractDamageDice, extractModifierAdjustments, extractModifiers, processDamageCategoryStacking } from "./synthetics";
import { DAMAGE_TYPE_CATEGORIES, isDamageDieSize, type DamageDieSize } from "./values";

export interface DerivedSpellDamage {
  domains: string[];
  base: BaseDamageData[];
  dice: DamageDice[];
  modifiers: Modifier[];
  kinds: ("damage" | "healing")[];
  formula: Record<Degree, string | null>;
  breakdown: Record<Degree, string[]>;
  /** False when a damage entry's formula is not simple dice and constants (a `@` term): the formula is then the entry's own text, with no modifiers. */
  simple: boolean;
}

interface RawDamageEntry {
  applyMod?: unknown;
  materials?: unknown;
  kinds?: unknown;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** The roll options a spell carries (Foundry: SpellPF2e#getRollOptions), prefixed `item:`. */
export function spellRollOptions(item: EngineItem, source: CastingSource, rank: number, damage: HeightenedSpell["damage"], cantrip: boolean): string[] {
  const options = [...item.getRollOptions("item", false), "magical", `item:rank:${rank}`];
  if (source.tradition) options.push(`item:trait:${source.tradition}`);
  if ((source.casting === "prepared" || source.casting === "spontaneous") && !cantrip) options.push("item:spell-slot");
  for (const d of damage) {
    if (d.type) options.push(`item:damage:${d.type}`, `item:damage:type:${d.type}`);
    const category = DAMAGE_TYPE_CATEGORIES[d.type] ?? null;
    if (category) options.push(`item:damage:category:${category}`);
    if (d.category === "persistent") options.push(`item:damage:persistent:${d.type}`);
  }
  const area = item.system.area;
  if (isRecord(area)) {
    options.push("item:area", "area-effect");
    if (typeof area.type === "string") options.push(`item:area:type:${area.type}`);
    if (typeof area.value === "number") options.push(`item:area:size:${area.value}`);
  }
  if (damage.some((d) => d.kinds.includes("damage"))) {
    options.push("damaging-effect");
    if (isRecord(area)) options.push("area-damage");
  }
  const defense = item.system.defense;
  if (isRecord(defense)) {
    const passive = isRecord(defense.passive) ? defense.passive : null;
    const save = isRecord(defense.save) ? defense.save : null;
    if (passive && typeof passive.statistic === "string") options.push(`item:defense:${passive.statistic}`);
    if (save && typeof save.statistic === "string") options.push(`item:defense:${save.statistic}`);
    if (save?.basic === true) options.push("item:defense:basic");
  }
  return [...new Set(options)];
}

/** Base damage entries from simple formulas: dice of one size per entry, the constant on the first. */
function baseFromFormula(formula: string, damageType: string, category: BaseDamageData["category"], materials: string[]): BaseDamageData[] | null {
  const terms = parseSimpleFormula(formula);
  if (terms === null) return null;
  const dice = new Map<number, number>();
  let constant = 0;
  for (const t of terms) {
    if ("constant" in t) constant += t.constant;
    else dice.set(t.faces, (dice.get(t.faces) ?? 0) + t.count);
  }
  const entries: BaseDamageData[] = [...dice.entries()]
    .filter(([faces, n]) => n > 0 && isDamageDieSize(`d${faces}`))
    .sort((a, b) => b[0] - a[0])
    .map(([faces, n]) => ({ damageType, category, diceNumber: n, dieSize: `d${faces}` as DamageDieSize, modifier: 0, materials }));
  if (entries.length === 0) {
    if (constant === 0 && terms.length > 0) return [];
    return constant !== 0 || terms.length === 0 ? [{ damageType, category, diceNumber: 0, dieSize: null, modifier: constant, materials }] : [];
  }
  entries[0]!.modifier = constant;
  return entries;
}

/** Foundry's SpellPF2e#getDamage for the spell at its cast rank; null when the spell deals no damage. */
export function calculateSpellDamage(state: EngineState, entry: Entry, heightened: HeightenedSpell, source: CastingSource, cantrip: boolean): DerivedSpellDamage | null {
  if (heightened.damage.length === 0) return null;
  const rawDamage = isRecord(heightened.system.damage) ? heightened.system.damage : {};
  const kinds = [...new Set(heightened.damage.flatMap((d) => d.kinds))].filter((k): k is "damage" | "healing" => k === "damage" || k === "healing");
  if (kinds.length === 0) kinds.push("damage");
  const item = new EngineItem({ entry });
  const isAttack = entry.traits.includes("attack");
  const domains = [...new Set([...kinds, ...kinds.map((k) => `spell-${k}`), ...kinds.map((k) => `${entry.slug}-${k}`), ...(isAttack ? ["attack-damage", "attack-spell-damage"] : [])])];
  const labels: FormulaLabels = {
    damageType: (type) => damageTypeLabel(state, type),
    persistent: (type) => localize(state, "PF2E.Damage.PersistentTooltip", "Persistent {damageType}", { damageType: damageTypeLabel(state, type) }),
    splash: localize(state, "PF2E.TraitSplash", "Splash"),
  };

  const base: BaseDamageData[] = [];
  let simple = true;
  for (const d of heightened.damage) {
    const raw = isRecord(rawDamage[d.id]) ? (rawDamage[d.id] as RawDamageEntry) : {};
    const materials = Array.isArray(raw.materials) ? raw.materials.filter((m): m is string => typeof m === "string") : [];
    const category = d.category === "persistent" || d.category === "precision" || d.category === "splash" ? d.category : null;
    const parsed = baseFromFormula(d.formula, d.type || "untyped", category, materials);
    if (parsed === null) {
      simple = false;
      break;
    }
    base.push(...parsed);
  }
  if (!simple) {
    // A formula the simple parser cannot read (a `@` term): keep the text, typed, with no modifiers.
    const instances = heightened.damage.map((d) => `(${d.formula})[${[d.type || "untyped", d.category === "persistent" ? "persistent" : null, ...d.kinds.filter((k) => k === "healing")].filter(Boolean).join(",")}]`);
    const formula = `{${instances.join(",")}}`;
    return { domains, base: [], dice: [], modifiers: [], kinds, formula: { failure: null, success: formula, criticalSuccess: formula }, breakdown: { failure: [], success: heightened.damage.map((d) => `${d.formula} ${damageTypeLabel(state, d.type || "untyped")}`), criticalSuccess: [] }, simple: false };
  }
  if (base.length === 0) return null;

  const options = new Set([...state.rollOptions.get(domains), ...spellRollOptions(item, source, heightened.rank, heightened.damage, cantrip), "action:cast-a-spell", "self:action:slug:cast-a-spell", ...entry.traits, ...(source.tradition ? [source.tradition] : [])]);
  const attribute = source.attribute;
  const attributeModifiers = heightened.damage
    .filter((d) => (isRecord(rawDamage[d.id]) ? (rawDamage[d.id] as RawDamageEntry).applyMod === true : false))
    .map((d) =>
      createModifier({
        label: localize(state, `PF2E.AbilityId.${attribute}`, attribute.charAt(0).toUpperCase() + attribute.slice(1)),
        slug: `ability-${d.id}`,
        type: "untyped",
        value: state.actor.system.abilities[attribute].mod,
        damageType: d.type || null,
        damageCategory: d.category === "persistent" || d.category === "precision" || d.category === "splash" ? d.category : null,
        alterations: extractDamageAlterations(state.synthetics.damageAlterations, domains, `ability-${d.id}`),
        adjustments: extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, `ability-${d.id}`),
      }),
    );
  const extractOptions = { selectors: domains, test: options, resolvables: { spell: item }, injectables: { spell: item } };
  const extracted = processDamageCategoryStacking(base, {
    modifiers: [...attributeModifiers, ...extractModifiers(state.synthetics, domains, extractOptions)],
    dice: extractDamageDice(state.synthetics, extractOptions),
    test: options,
  });
  for (const dice of extracted.dice) for (const alteration of dice.alterations) applyDamageAlteration(alteration, dice, { item, test: options });
  for (const modifier of extracted.modifiers) for (const alteration of modifier.alterations) applyDamageAlteration(alteration, modifier, { item, test: options });

  const formulaData = { base, dice: extracted.dice, modifiers: extracted.modifiers, kinds };
  const computed: Record<Degree, ReturnType<typeof createDamageFormula>> = {
    failure: createDamageFormula(formulaData, "failure", labels),
    success: createDamageFormula(formulaData, "success", labels),
    criticalSuccess: createDamageFormula(formulaData, "criticalSuccess", labels),
  };
  return {
    domains,
    base,
    dice: extracted.dice,
    modifiers: extracted.modifiers,
    kinds,
    formula: { failure: computed.failure?.formula ?? null, success: computed.success?.formula ?? null, criticalSuccess: computed.criticalSuccess?.formula ?? null },
    breakdown: { failure: computed.failure?.breakdown ?? [], success: computed.success?.breakdown ?? [], criticalSuccess: computed.criticalSuccess?.breakdown ?? [] },
    simple: true,
  };
}
